import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { getStorage, setStorage } from "./utils";
import type { AuthIdentity } from "./auth";
import i18n from "./i18n/config";

export type PlayerLobbyData = {
  points: number;
  is_ready: boolean;
  name: string;
  /** Provider of the player's most recent sign-in; bots have none. */
  provider?: string | null;
  image?: string | null;
};

export type LobbyState = {
  players: Record<string, PlayerLobbyData>;
  host_id: string;
};

type AppStore = {
  lang: string;
  name: string;
  id: string;
  auth: AuthIdentity | null;
  isDark: boolean;
  activeDate: "today" | "yesterday";
  setLanguage: (lang: string) => void;
  setActiveDate: (date: "today" | "yesterday") => void;
  setAuthIdentity: (identity: AuthIdentity) => void;
  toggleTheme: () => void;
  restartGame: () => { action: "restart_game" };
  kickPlayer: (playerId: string) => { action: string; payload?: unknown };
};

const SUPPORTED_LANGS = ["en", "de", "es", "fr", "sl"];

function detectLanguage(): string {
  const stored = getStorage("lang");
  if (stored) return stored;

  const browserLangs = navigator.languages ?? [navigator.language];
  for (const bl of browserLangs) {
    const code = bl.split("-")[0].toLowerCase();
    if (SUPPORTED_LANGS.includes(code)) return code;
  }
  return "en";
}

/** Mirrors isDark onto <html> so CSS can style night mode (see index.css). */
function applyThemeAttribute(isDark: boolean) {
  document.documentElement.dataset.theme = isDark ? "dark" : "light";
}

applyThemeAttribute(getStorage("isDark") !== "false");

const detectedLang = detectLanguage();
i18n.changeLanguage(detectedLang);

export const useAppStore = create<AppStore>()(
  immer((set) => ({
    lang: detectedLang,
    name: "",
    id: "",
    auth: null,
    isDark: getStorage("isDark") !== "false",
    activeDate:
      (getStorage("activeDate") as "today" | "yesterday") || "yesterday",
    setLanguage: (lang: string) => {
      setStorage("lang", lang);
      i18n.changeLanguage(lang);
      set((state) => void (state.lang = lang));
    },
    setActiveDate: (date: "today" | "yesterday") => {
      setStorage("activeDate", date);
      set((state) => void (state.activeDate = date));
    },
    setAuthIdentity: (identity) => {
      set((state) => {
        state.id = identity.id;
        state.name = identity.name;
        state.auth = identity;
      });
    },
    toggleTheme: () => {
      set((state) => {
        state.isDark = !state.isDark;
        setStorage("isDark", String(state.isDark));
        applyThemeAttribute(state.isDark);
      });
    },
    //TODO: create lobbyState and move this into lobbyState
    restartGame: () => {
      return {
        action: "restart_game",
      };
    },
    kickPlayer: (playerId: string) => {
      return {
        action: "kick_player",
        payload: { id: playerId },
      };
    },
  })),
);
